import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { integrationsService } from "../services/integrationsService";
import { PlayCircle, RefreshCw, Power, Plug } from "lucide-react";
import PageHeader from "../components/common/PageHeader";
import IntegrationLogo from "../components/common/IntegrationLogo";
import { formatDateTime } from "../utils/dateFormatters";
import {
  INTEGRATION_REGISTRY,
  COMING_SOON_IDS,
  HIDDEN_IDS,
  API_KEY_IDS,
  ICON_INVERT_IDS,
  BETA_IDS,
  COMPOSIO_IDS,
  getConnectionSource,
} from "../config/integrations";
import { ComposioService } from "../services/composioService";
import { useIntegrationModals } from "../hooks/useIntegrationModals";
import "../styles/integrations.css";

// Runtime integration state (extends config with connection info)
export interface Integration {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  color: string;
  isConnected: boolean;
  connectionStatus: "connected" | "disconnected" | "error" | "connecting";
  lastSync?: string;
  authType: string;
  configUrl?: string;
  docsUrl: string;
  features: string[];
  domain?: string;
}

// All integrations available on the settings page, derived from the shared registry
// Hidden integrations are excluded from the UI entirely
const AVAILABLE_INTEGRATIONS = INTEGRATION_REGISTRY.filter(
  (i) => !HIDDEN_IDS.has(i.id),
);

const Integrations: React.FC = () => {
  const { user } = useAuth();
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [composioToolkits, setComposioToolkits] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [connectingIntegration, setConnectingIntegration] = useState<
    string | null
  >(null);
  const [testingIntegration, setTestingIntegration] = useState<string | null>(
    null
  );
  const [testResults, setTestResults] = useState<any>(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [disconnectingIntegration, setDisconnectingIntegration] = useState<
    string | null
  >(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [reauthenticatingIntegration, setReauthenticatingIntegration] =
    useState<string | null>(null);
  // Shared pre-connection modal hook (GitHub org warning + API-key info modals)
  // Uses a ref so the hook callback always calls the latest doConnect without
  // needing it to be defined before the hook.
  const doConnectRef = React.useRef<((id: string) => void) | null>(null);
  const { preConnectCheck, renderModals } = useIntegrationModals(
    (id: string) => doConnectRef.current?.(id),
  );

  // Categories are computed dynamically to include Composio toolkit counts
  const allForCounts = [...integrations, ...composioToolkits];
  const categories = [
    {
      id: "all",
      name: "All Integrations",
      count: allForCounts.length,
    },
    {
      id: "development",
      name: "Development",
      count: allForCounts.filter((i) => i.category === "development").length,
    },
    {
      id: "project-management",
      name: "Project Management",
      count: allForCounts.filter((i) => i.category === "project-management").length,
    },
    {
      id: "communication",
      name: "Communication",
      count: allForCounts.filter((i) => i.category === "communication").length,
    },
    {
      id: "cloud",
      name: "Cloud & DevOps",
      count: allForCounts.filter((i) => i.category === "cloud").length,
    },
    {
      id: "analytics",
      name: "Analytics & Finance",
      count: allForCounts.filter((i) => i.category === "analytics").length,
    },
    {
      id: "crm",
      name: "CRM & Sales",
      count: allForCounts.filter((i) => i.category === "crm").length,
    },
    {
      id: "productivity",
      name: "Productivity",
      count: allForCounts.filter((i) => i.category === "productivity").length,
    },
  ];

  // Detect Composio OAuth callback and show success toast.
  //
  // New connect links land on /oauth/composio/callback instead, which closes
  // the popup and posts OAUTH_SUCCESS (handled by the listener below). This
  // branch is kept for Connect Links minted before that change — they carry the
  // old return_url and will keep arriving here until they expire.
  const [composioToast, setComposioToast] = useState<string | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("composio_callback") === "true") {
      const provider = params.get("provider");
      setComposioToast(provider ? `${provider} connected successfully!` : "Integration connected successfully!");
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
      // Auto-dismiss after 5s
      setTimeout(() => setComposioToast(null), 5000);
    }
  }, []);

  useEffect(() => {
    loadIntegrations();
  }, [user]);

  const loadIntegrations = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Get user's connected integrations
      const connectedIntegrations =
        await integrationsService.getUserIntegrations();

      // Merge static registry integrations with connection status
      const integrationsWithStatus = AVAILABLE_INTEGRATIONS.map((available) => {
        const connected = connectedIntegrations.find(
          (conn) => conn.provider === available.id
        );
        const isActive = connected?.integration_status === "active";
        return {
          ...available,
          isConnected: !!connected && isActive,
          connectionStatus: (connected
            ? isActive
              ? "connected"
              : "error"
            : "disconnected") as Integration["connectionStatus"],
          lastSync: connected?.updated_at,
        };
      });

      setIntegrations(integrationsWithStatus);

      // Fetch all Composio toolkits dynamically (950+ apps)
      try {
        const toolkits = await ComposioService.listToolkits();
        // Normalize function: strip dashes, underscores, spaces for comparison
        const normalize = (s: string) => s.toLowerCase().replace(/[-_\s]+/g, "");
        // Build sets from static registry for dedup
        const staticIds = new Set(AVAILABLE_INTEGRATIONS.map((i) => i.id));
        const staticNormalized = new Set(AVAILABLE_INTEGRATIONS.map((i) => normalize(i.id)));
        const staticNames = new Set(AVAILABLE_INTEGRATIONS.map((i) => normalize(i.name)));
        const seenNormalized = new Set<string>();
        const dynamicToolkits: Integration[] = toolkits
          .map((t: any) => {
            const id = (t.key || t.appId || t.name || "").toLowerCase();
            return { ...t, _id: id };
          })
          .filter((t: any) => {
            if (!t._id) return false;
            const norm = normalize(t._id);
            const normName = normalize(t.name || "");
            // Skip if matches any static entry or already seen
            if (staticIds.has(t._id) || staticNormalized.has(norm) || seenNormalized.has(norm)) return false;
            if (staticNames.has(normName)) return false;
            seenNormalized.add(norm);
            return true;
          })
          .map((t: any) => {
            const id = t._id;
            const connected = connectedIntegrations.find(
              (conn: any) => conn.provider === id
            );
            const isActive = connected?.integration_status === "active";
            return {
              id,
              name: t.name || t.key || "Unknown",
              description: t.description || `Connect ${t.name || t.key} via Composio`,
              category: (t.categories?.[0]?.toLowerCase() || "cloud") as string,
              icon: t.logo || `https://logo.clearbit.com/${id}.com`,
              color: "#7C3AED",
              isConnected: !!connected && isActive,
              connectionStatus: (connected
                ? isActive ? "connected" : "error"
                : "disconnected") as Integration["connectionStatus"],
              lastSync: connected?.updated_at,
              authType: t.authSchemes?.[0] || "oauth",
              docsUrl: "",
              features: [],
            };
          });
        setComposioToolkits(dynamicToolkits);
      } catch (err) {
        console.error("Failed to load Composio toolkits:", err);
      }
    } catch (error) {
      console.error("Failed to load integrations:", error);
    } finally {
      setLoading(false);
    }
  };

  // Actual connection logic (called directly or after modal confirmation)
  const doConnect = async (integrationId: string) => {
    try {
      setConnectingIntegration(integrationId);

      // Update local state to show connecting status
      setIntegrations((prev) =>
        prev.map((integration) =>
          integration.id === integrationId
            ? { ...integration, connectionStatus: "connecting" }
            : integration
        )
      );

      // Route Composio integrations through ComposioService
      // Includes both static COMPOSIO_IDS and any dynamically-loaded toolkit
      const isDynamicComposio = composioToolkits.some((t) => t.id === integrationId);
      if (COMPOSIO_IDS.has(integrationId) || isDynamicComposio) {
        try {
          const result = await ComposioService.initiateConnection(integrationId);
          const redirectUrl = result.redirectUrl;

          if (!redirectUrl) {
            setConnectingIntegration(null);
            setComposioToast(`${integrationId} doesn't support managed OAuth. It may require custom setup.`);
            setTimeout(() => setComposioToast(null), 5000);
            setIntegrations((prev) =>
              prev.map((i) => i.id === integrationId ? { ...i, connectionStatus: "disconnected" } : i)
            );
            return;
          }

          // Open in centered popup window
          const width = 500;
          const height = 700;
          const left = window.screenX + (window.outerWidth - width) / 2;
          const top = window.screenY + (window.outerHeight - height) / 2;
          window.open(
            redirectUrl,
            `composio_connect_${integrationId}`,
            `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`
          );

          // Poll for connection completion
          let attempts = 0;
          const pollInterval = setInterval(async () => {
            attempts++;
            try {
              const status = await ComposioService.getConnectionStatus(integrationId);
              if (status.connected) {
                clearInterval(pollInterval);
                setConnectingIntegration(null);
                setComposioToast(`${integrationId} connected successfully!`);
                setTimeout(() => setComposioToast(null), 5000);
                await loadIntegrations();
                return;
              }
            } catch { /* ignore polling errors */ }
            if (attempts >= 30) {
              clearInterval(pollInterval);
              setConnectingIntegration(null);
              setComposioToast(`${integrationId} connection is being processed. Please refresh in a moment.`);
              setTimeout(() => setComposioToast(null), 6000);
              setIntegrations((prev) =>
                prev.map((i) =>
                  i.id === integrationId
                    ? { ...i, connectionStatus: "disconnected" }
                    : i
                )
              );
            }
          }, 2000);
        } catch (error) {
          console.error(`Failed to connect Composio integration ${integrationId}:`, error);
          setComposioToast(`Unable to connect ${integrationId}. This integration may not be available yet.`);
          setTimeout(() => setComposioToast(null), 5000);
          setIntegrations((prev) =>
            prev.map((i) =>
              i.id === integrationId
                ? { ...i, connectionStatus: "disconnected" }
                : i
            )
          );
          setConnectingIntegration(null);
        }
        return;
      }

      // Initiate OAuth flow with callbacks (Nango / custom OAuth)
      await integrationsService.connectIntegration(integrationId, {
        onSuccess: async () => {
          console.log(`✅ Successfully connected ${integrationId}`);

          // Retry checking for the connection with exponential backoff
          // This gives the Nango webhook time to process and store the connection
          const checkConnection = async (
            retries = 5,
            delay = 500
          ): Promise<boolean> => {
            for (let i = 0; i < retries; i++) {
              try {
                // Reload integrations from server
                await loadIntegrations();

                // Fast DB-only check — webhook should have created the record
                const connectedIntegrations =
                  await integrationsService.getUserIntegrations();
                const isConnected = connectedIntegrations.some(
                  (conn) =>
                    conn.provider === integrationId &&
                    conn.integration_status === "active"
                );

                if (isConnected) {
                  console.log(
                    `✅ Connection verified for ${integrationId} after ${
                      i + 1
                    } attempt(s)`
                  );
                  return true;
                }

                // Wait before next retry with exponential backoff
                if (i < retries - 1) {
                  const waitTime = delay * Math.pow(2, i);
                  console.log(
                    `⏳ Waiting ${waitTime}ms before retry ${
                      i + 2
                    }/${retries}...`
                  );
                  await new Promise((resolve) => setTimeout(resolve, waitTime));
                }
              } catch (error) {
                console.error(`Retry ${i + 1} failed:`, error);
              }
            }
            return false;
          };

          const connected = await checkConnection();
          setConnectingIntegration(null);

          if (!connected) {
            console.warn(
              `⚠️ Connection for ${integrationId} not found after retries. Webhook may still be processing.`
            );
            // Update state to show error
            setIntegrations((prev) =>
              prev.map((integration) =>
                integration.id === integrationId
                  ? { ...integration, connectionStatus: "error" }
                  : integration
              )
            );
          }
        },
        onError: (error: Error) => {
          console.error(`❌ Failed to connect ${integrationId}:`, error);

          // Reset connection status on error
          setIntegrations((prev) =>
            prev.map((integration) =>
              integration.id === integrationId
                ? { ...integration, connectionStatus: "error" }
                : integration
            )
          );
          setConnectingIntegration(null);
        },
        onClose: () => {
          console.log(`ℹ️ User closed ${integrationId} connection dialog`);

          // Reset connecting state to disconnected
          setIntegrations((prev) =>
            prev.map((integration) =>
              integration.id === integrationId
                ? { ...integration, connectionStatus: "disconnected" }
                : integration
            )
          );
          setConnectingIntegration(null);
        },
      });

      // Note: For legacy OAuth (redirect-based), the page will redirect away
      // For Nango (modal-based), callbacks above handle state updates
    } catch (error) {
      console.error(`Failed to connect ${integrationId}:`, error);

      // Reset connection status on error
      setIntegrations((prev) =>
        prev.map((integration) =>
          integration.id === integrationId
            ? { ...integration, connectionStatus: "error" }
            : integration
        )
      );
      setConnectingIntegration(null);
    }
  };
  doConnectRef.current = doConnect;

  const handleConnect = (integrationId: string) => {
    if (!preConnectCheck(integrationId)) return;
    doConnect(integrationId);
  };

  const handleDisconnect = async (integrationId: string) => {
    // Show confirmation modal instead of immediately disconnecting
    setDisconnectingIntegration(integrationId);
    setShowDisconnectModal(true);
  };

  const confirmDisconnect = async () => {
    if (!disconnectingIntegration) return;

    setIsDisconnecting(true);
    try {
      // Route disconnect through correct service
      const isComposioDisconnect = COMPOSIO_IDS.has(disconnectingIntegration) ||
        composioToolkits.some((t) => t.id === disconnectingIntegration);
      if (isComposioDisconnect) {
        await ComposioService.deleteConnection(disconnectingIntegration);
      } else {
        await integrationsService.disconnectIntegration(disconnectingIntegration);
      }

      // Update local state
      setIntegrations((prev) =>
        prev.map((integration) =>
          integration.id === disconnectingIntegration
            ? {
                ...integration,
                isConnected: false,
                connectionStatus: "disconnected",
                lastSync: undefined,
              }
            : integration
        )
      );
    } catch (error) {
      console.error(`Failed to disconnect ${disconnectingIntegration}:`, error);
    } finally {
      // Close modal and reset state
      setIsDisconnecting(false);
      setShowDisconnectModal(false);
      setDisconnectingIntegration(null);
    }
  };

  const cancelDisconnect = () => {
    setShowDisconnectModal(false);
    setDisconnectingIntegration(null);
  };

  const handleTestConnection = async (integrationId: string) => {
    try {
      setTestingIntegration(integrationId);
      console.log(`🧪 Testing ${integrationId} integration...`);

      const results = await integrationsService.testIntegration(integrationId);
      console.log(`✅ Test results for ${integrationId}:`, results);

      setTestResults({
        ...results,
        provider: integrationId,
        timestamp: new Date().toISOString(),
      });
      setShowTestModal(true);
    } catch (error) {
      console.error(`Connection test failed for ${integrationId}:`, error);
      setTestResults({
        provider: integrationId,
        status: "error",
        error: error instanceof Error ? error.message : "Unknown error",
        timestamp: new Date().toISOString(),
      });
      setShowTestModal(true);
    } finally {
      setTestingIntegration(null);
    }
  };

  // Listen for popup-based OAuth completion
  useEffect(() => {
    const listener = (event: MessageEvent) => {
      if (!event || !event.data || typeof event.data !== "object") return;
      const { type, provider } = event.data as any;
      if (type === "OAUTH_SUCCESS" && provider) {
        loadIntegrations();
        setReauthenticatingIntegration(null);
      } else if (type === "OAUTH_ERROR" && provider) {
        setReauthenticatingIntegration(null);
      } else if (type === "OAUTH_CLOSED" && provider) {
        // User closed the popup without completing OAuth - reset button state
        console.log(`ℹ️ User closed ${provider} reconnection popup without completing`);
        setReauthenticatingIntegration(null);
      }
    };
    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, []);

  const handleReconnect = async (integrationId: string) => {
    try {
      setReauthenticatingIntegration(integrationId);
      await integrationsService.reauthenticateIntegration(integrationId);
    } catch (error) {
      console.error(`Failed to reauthenticate ${integrationId}:`, error);
      setReauthenticatingIntegration(null);
    }
  };

  // Combine static registry + dynamic Composio toolkits
  const allIntegrations = [...integrations, ...composioToolkits];

  const filteredIntegrations = allIntegrations.filter((integration) => {
    const matchesCategory =
      selectedCategory === "all" || integration.category === selectedCategory;
    const matchesSearch =
      integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      integration.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Pagination
  const totalFiltered = filteredIntegrations.length;
  const totalPages = Math.ceil(totalFiltered / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedIntegrations = filteredIntegrations.slice(startIndex, startIndex + itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => { setCurrentPage(1); }, [searchQuery, selectedCategory]);

  const connectedCount = allIntegrations.filter((i) => i.isConnected).length;

  if (loading) {
    return (
      <div className="su-integrations-loading">
        <div className="su-integrations-loading-spinner"></div>
        <p className="su-integrations-loading-text">Loading integrations...</p>
      </div>
    );
  }

  return (
    <div className="w-full px-9 py-6">
      {composioToast && (
        <div className="fixed top-4 right-4 z-50 bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in">
          <span className="text-lg">&#x2705;</span>
          <span>{composioToast}</span>
          <button onClick={() => setComposioToast(null)} className="ml-2 text-white/80 hover:text-white">&times;</button>
        </div>
      )}
      <PageHeader
        title="Integrations"
        subtitle="Connect your favorite tools to automatically gather data for your email updates"
        action={
          <button onClick={loadIntegrations} className="su-btn-secondary">
            <RefreshCw size={16} style={{ marginRight: 8 }} />
            REFRESH ALL
          </button>
        }
      />

      <div className="su-integrations-stats su-section mt-6">
        <p className="su-integrations-stat-text">
          <strong>{connectedCount}</strong> of {allIntegrations.length}{" "}
          integrations connected
        </p>
      </div>

      {/* Search bar */}
      <div style={{ marginBottom: "16px" }}>
        <input
          type="text"
          placeholder={`Search ${allIntegrations.length}+ integrations...`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="su-profile-input"
          style={{ width: "100%", maxWidth: "500px", padding: "10px 16px", fontSize: "14px" }}
        />
      </div>

      <div className="su-integrations-content">
        <div className="su-integrations-sidebar">
          <div className="su-section su-integrations-categories-section">
            <h3 className="su-integrations-categories-title">Categories</h3>
            <ul className="su-integrations-categories-list">
              {categories.map((category) => (
                <li key={category.id}>
                  <button
                    className={`su-integrations-category-btn ${
                      selectedCategory === category.id ? "active" : ""
                    }`}
                    onClick={() => setSelectedCategory(category.id)}
                  >
                    <span>{category.name}</span>
                    <span className="su-integrations-category-count">
                      {category.count}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div style={{ flex: 1 }}>
          {/* Pagination info + per-page selector */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", fontSize: "13px", color: "#999" }}>
            <span>
              Showing {totalFiltered === 0 ? 0 : startIndex + 1}–{Math.min(startIndex + itemsPerPage, totalFiltered)} of {totalFiltered} integrations
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>Per page:</span>
              {[25, 50, 100].map((n) => (
                <button
                  key={n}
                  onClick={() => { setItemsPerPage(n); setCurrentPage(1); }}
                  style={{
                    padding: "4px 10px",
                    fontSize: "12px",
                    background: itemsPerPage === n ? "#7C3AED" : "transparent",
                    color: itemsPerPage === n ? "#fff" : "#999",
                    border: "1px solid #333",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          <div className="su-integrations-grid">
            {paginatedIntegrations.map((integration) => (
              <IntegrationCard
                key={integration.id}
                integration={integration}
                onConnect={() => handleConnect(integration.id)}
                onDisconnect={() => handleDisconnect(integration.id)}
                onReconnect={() => handleReconnect(integration.id)}
                onTest={() => handleTestConnection(integration.id)}
                isConnecting={connectingIntegration === integration.id}
                isReauthenticating={
                  reauthenticatingIntegration === integration.id
                }
                isTesting={testingIntegration === integration.id}
              />
            ))}
          </div>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px", marginTop: "24px", paddingBottom: "24px" }}>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{ padding: "6px 14px", fontSize: "13px", background: "transparent", color: currentPage === 1 ? "#555" : "#fff", border: "1px solid #333", borderRadius: "4px", cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
              >
                Previous
              </button>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let page: number;
                if (totalPages <= 7) {
                  page = i + 1;
                } else if (currentPage <= 4) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 3) {
                  page = totalPages - 6 + i;
                } else {
                  page = currentPage - 3 + i;
                }
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    style={{
                      padding: "6px 12px",
                      fontSize: "13px",
                      background: currentPage === page ? "#7C3AED" : "transparent",
                      color: currentPage === page ? "#fff" : "#999",
                      border: "1px solid #333",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontWeight: currentPage === page ? 700 : 400,
                    }}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={{ padding: "6px 14px", fontSize: "13px", background: "transparent", color: currentPage === totalPages ? "#555" : "#fff", border: "1px solid #333", borderRadius: "4px", cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Pre-connection modals (GitHub org warning + API key info modals) */}
      {renderModals()}

      {/* Disconnect Confirmation Modal */}
      {showDisconnectModal && disconnectingIntegration && (
        <ConfirmDisconnectModal
          integrationName={
            integrations.find((i) => i.id === disconnectingIntegration)?.name ||
            disconnectingIntegration
          }
          isLoading={isDisconnecting}
          onConfirm={confirmDisconnect}
          onCancel={cancelDisconnect}
        />
      )}

      {/* Test Results Modal */}
      {showTestModal && testResults && (
        <TestResultsModal
          results={testResults}
          onClose={() => {
            setShowTestModal(false);
            setTestResults(null);
          }}
        />
      )}
    </div>
  );
};

interface IntegrationCardProps {
  integration: Integration;
  onConnect: () => void;
  onDisconnect: () => void;
  onReconnect: () => void;
  onTest: () => void;
  isConnecting: boolean;
  isReauthenticating: boolean;
  isTesting: boolean;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  integration,
  onConnect,
  onDisconnect,
  onReconnect,
  onTest,
  isConnecting,
  isReauthenticating,
  isTesting,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const requiresApiKey = API_KEY_IDS.has(integration.id);
  const isComingSoon = COMING_SOON_IDS.has(integration.id);
  const isBeta = BETA_IDS.has(integration.id);

  const getStatusBadge = () => {
    switch (integration.connectionStatus) {
      case "connected":
        return <span className="su-badge-active">CONNECTED</span>;
      case "connecting":
        return <span className="su-badge-paused">CONNECTING...</span>;
      case "error":
        return <span className="su-badge-paused">ERROR</span>;
      default:
        return <span className="su-badge-paused">NOT CONNECTED</span>;
    }
  };

  return (
    <div
      className={`su-integration-card ${
        integration.isConnected ? "connected" : ""
      }`}
      style={{ cursor: "pointer" }}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <div className="su-integration-card-header">
        <div className="su-integration-icon-wrapper">
          <IntegrationLogo
            domain={integration.domain}
            name={integration.name}
            fallbackImage={integration.icon}
            className={`w-full h-full ${
              ICON_INVERT_IDS.has(integration.id) ? "su-icon-invert" : ""
            }`}
          />
        </div>
        <div className="su-integration-info">
          <h3 className="su-integration-name">{integration.name}</h3>
          <div style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" }}>
            {isComingSoon ? (
              <span className="su-badge-coming-soon">COMING SOON</span>
            ) : (
              getStatusBadge()
            )}
            {requiresApiKey && <span className="su-badge-apikey">API KEY</span>}
            {isBeta && <span className="su-badge-beta">BETA</span>}
          </div>
        </div>
      </div>

      {isExpanded && (
        <>
          <p className="su-integration-description">{integration.description}</p>

          {integration.features.length > 0 && (
            <div className="su-integration-features">
              <span className="su-integration-features-label">Features</span>
              <div className="su-integration-features-list">
                {integration.features.map((feature) => (
                  <span key={feature} className="su-integration-feature-tag">
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          )}

          {integration.lastSync && (
            <div className="su-integration-last-sync">
              Last synced: {formatDateTime(integration.lastSync)}
            </div>
          )}
        </>
      )}

      <div className="su-integration-actions" onClick={(e) => e.stopPropagation()}>
        {isComingSoon ? (
          <button
            className="su-btn-secondary"
            disabled
            style={{ width: "100%", opacity: 0.5, cursor: "not-allowed" }}
          >
            Coming Soon
          </button>
        ) : integration.isConnected ? (
          <>
            <button
              className="su-btn-secondary"
              onClick={onTest}
              disabled={isTesting}
            >
              <PlayCircle size={16} />
              {isTesting ? "Testing..." : "Test"}
            </button>
            <button
              className="su-btn-secondary"
              onClick={onReconnect}
              disabled={isReauthenticating}
            >
              <RefreshCw size={16} />
              {isReauthenticating ? "Reconnecting..." : "Reconnect"}
            </button>
            <button className="su-btn-danger" onClick={onDisconnect}>
              <Power size={16} />
              Disconnect
            </button>
          </>
        ) : (
          <button
            className="su-btn-primary"
            onClick={onConnect}
            disabled={isConnecting}
            style={{ width: "100%" }}
          >
            <Plug size={16} />
            {isConnecting ? "Connecting..." : "Connect"}
          </button>
        )}
      </div>
    </div>
  );
};

interface TestResultsModalProps {
  results: any;
  onClose: () => void;
}

const TestResultsModal: React.FC<TestResultsModalProps> = ({
  results,
  onClose,
}) => {
  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return "null";
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        return `Array (${value.length} items)`;
      }
      return `Object (${Object.keys(value).length} keys)`;
    }
    if (typeof value === "string" && value.length > 100) {
      return value.substring(0, 100) + "...";
    }
    return String(value);
  };

  const renderDataSection = (data: any, title: string) => {
    if (!data || (typeof data === "object" && Object.keys(data).length === 0)) {
      return null;
    }

    return (
      <div className="su-test-data-section">
        <h4 className="su-test-data-title">{title}</h4>
        <div>
          {typeof data === "object" ? (
            Object.entries(data).map(([key, value]) => (
              <div key={key} className="su-test-data-row">
                <span className="su-test-data-key">{key}:</span>
                <span className="su-test-data-value">{formatValue(value)}</span>
              </div>
            ))
          ) : (
            <div className="su-test-data-value">{formatValue(data)}</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="su-integration-modal-overlay" onClick={onClose}>
      <div
        className="su-integration-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="su-integration-modal-header">
          <h3 className="su-integration-modal-title">
            Test Results - {results.provider}
          </h3>
          <button
            className="su-integration-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <div className="su-integration-modal-body">
          <div className="su-test-summary">
            <div
              className={`su-test-status ${
                results.status === "success" ? "success" : "error"
              }`}
            >
              {results.status === "success" ? "✅" : "❌"}{" "}
              {results.status.toUpperCase()}
            </div>
            {results.response_time_ms && (
              <div className="su-test-details">
                Response Time: {results.response_time_ms}ms
              </div>
            )}
            <div className="su-test-details">
              Tested at: {formatDateTime(results.timestamp)}
            </div>
          </div>

          {results.error && (
            <div className="su-test-data-section">
              <h4 className="su-test-data-title">Error Details</h4>
              <div
                className="su-test-details"
                style={{ color: "var(--su-red-500)" }}
              >
                {results.error}
              </div>
            </div>
          )}

          {results.data_summary && (
            <div className="su-test-data-section">
              <h4 className="su-test-data-title">Connection Summary</h4>
              <div className="su-summary-grid">
                <div className="su-summary-item">
                  <span className="su-summary-label">Health Status:</span>
                  <span
                    className={`su-summary-value ${
                      results.data_summary.connection_healthy
                        ? "healthy"
                        : "unhealthy"
                    }`}
                  >
                    {results.data_summary.connection_healthy
                      ? "✅ Healthy"
                      : "❌ Unhealthy"}
                  </span>
                </div>
                {results.data_summary.total_data_points && (
                  <div className="su-summary-item">
                    <span className="su-summary-label">Data Points:</span>
                    <span className="su-summary-value">
                      {results.data_summary.total_data_points}
                    </span>
                  </div>
                )}
                {results.data_summary.last_successful_fetch && (
                  <div className="su-summary-item">
                    <span className="su-summary-label">Last Fetch:</span>
                    <span className="su-summary-value">
                      {formatDateTime(results.data_summary.last_successful_fetch)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {results.data_keys && results.data_keys.length > 0 && (
            <div className="su-data-keys-section">
              <h4>Available Data Types ({results.data_keys.length})</h4>
              <div className="su-data-keys-list">
                {results.data_keys.map((key: string) => (
                  <span key={key} className="su-data-key-tag">
                    {key}
                  </span>
                ))}
              </div>
            </div>
          )}

          {results.sample_data &&
            Object.keys(results.sample_data).length > 0 &&
            renderDataSection(
              results.sample_data,
              `Sample ${
                results.provider.charAt(0).toUpperCase() +
                results.provider.slice(1)
              } Data`
            )}

          {results.test_result &&
            renderDataSection(results.test_result, "Raw Test Data")}
        </div>

        <div className="su-integration-modal-footer">
          <button className="su-btn-primary" onClick={onClose}>
            CLOSE
          </button>
        </div>
      </div>
    </div>
  );
};

interface ConfirmDisconnectModalProps {
  integrationName: string;
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmDisconnectModal: React.FC<ConfirmDisconnectModalProps> = ({
  integrationName,
  isLoading,
  onConfirm,
  onCancel,
}) => {
  return (
    <div className="su-integration-modal-overlay" onClick={isLoading ? undefined : onCancel}>
      <div
        className="su-integration-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="su-integration-modal-header">
          <h3 className="su-integration-modal-title">Disconnect Integration</h3>
          <button
            className="su-integration-modal-close"
            onClick={onCancel}
            disabled={isLoading}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <div className="su-integration-modal-body">
          <div className="su-confirm-message">
            <div className="su-confirm-icon">⚠️</div>
            <div className="su-confirm-text">
              <p>
                Are you sure you want to disconnect{" "}
                <strong>{integrationName}</strong>?
              </p>
              <p className="su-confirm-warning">
                This will remove access to your {integrationName} data and stop
                any automated workflows that depend on this integration.
              </p>
            </div>
          </div>
        </div>

        <div className="su-integration-modal-footer">
          <button className="su-btn-secondary" onClick={onCancel} disabled={isLoading}>
            CANCEL
          </button>
          <button className="su-btn-danger" onClick={onConfirm} disabled={isLoading}>
            {isLoading ? "DISCONNECTING..." : "DISCONNECT"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Integrations;
